import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, getRoleCapabilities } from '../utils/permissions';
import { CircularProgress, Box, Alert } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
  requiresPermission?: {
    category: string;
    action: string;
  };
  requiresCapability?: string;
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  adminOnly = false, 
  requiresPermission,
  requiresCapability,
  fallbackPath = '/admin/dashboard'
}) => {
  const { user, loading, token } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!token || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Define admin-level roles that can access admin routes
  const adminRoles = [
    'super_admin',
    'admin', 
    'form_manager',
    'payment_approver',
    'submission_viewer',
    'submission_editor',
    'notification_manager'
  ];

  if (adminOnly && !adminRoles.includes(user.role)) {
    return <Navigate to="/login" replace />;
  }

  // Check specific permission requirement
  if (requiresPermission) {
    const { category, action } = requiresPermission;
    if (!hasPermission(user, category, action)) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Access denied. You don't have permission to access this page.
            Required permission: {category}.{action}
          </Alert>
        </Box>
      );
    }
  }

  // Check capability requirement
  if (requiresCapability) {
    const capabilities = getRoleCapabilities(user.role);
    const hasCapability = capabilities[requiresCapability as keyof typeof capabilities];
    
    if (!hasCapability) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Access denied. You don't have permission to access this page.
            Your role ({user.role}) doesn't have the required capability: {requiresCapability}
          </Alert>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
